import type { InvalidateQueryFilters } from "@tanstack/react-query";
import { onlineManager, QueryClient, QueryKey } from "@tanstack/react-query";

type OfflineReplayStatus = "idle" | "replaying" | "paused";

let status: OfflineReplayStatus = "idle";
const deferredInvalidations = new Map<string, InvalidateQueryFilters>();

function keyFor(filters: InvalidateQueryFilters): string {
  return JSON.stringify(filters.queryKey ?? []);
}

export function getOfflineReplayStatus(): OfflineReplayStatus {
  return status;
}

export function isOfflineReplayActive(): boolean {
  return status === "replaying" || status === "paused";
}

export function deferQueryInvalidation(filters: InvalidateQueryFilters): void {
  deferredInvalidations.set(keyFor(filters), filters);
}

export function invalidateUnlessReplaying(
  queryClient: QueryClient,
  filters: InvalidateQueryFilters
): Promise<void> {
  if (isOfflineReplayActive()) {
    deferQueryInvalidation(filters);
    return Promise.resolve();
  }
  return queryClient.invalidateQueries(filters);
}

async function flushDeferredInvalidations(
  queryClient: QueryClient
): Promise<void> {
  const invalidations = Array.from(deferredInvalidations.values());
  deferredInvalidations.clear();
  await Promise.all(
    invalidations.map((filters) => queryClient.invalidateQueries(filters))
  );
}

function normalizeReplayError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

export async function resumePausedMutationsWithoutPartialRefetch(
  queryClient: QueryClient,
  queryKeysToRefreshAfterReplay: QueryKey[] = []
): Promise<void> {
  status = "replaying";
  let replayError: Error | undefined;
  try {
    await queryClient.resumePausedMutations();
  } catch (error) {
    replayError = normalizeReplayError(error);
  }
  if (!onlineManager.isOnline()) {
    status = "paused";
    if (replayError) throw replayError;
    return;
  }
  for (const queryKey of queryKeysToRefreshAfterReplay) {
    deferQueryInvalidation({ queryKey });
  }
  status = "idle";
  await flushDeferredInvalidations(queryClient);
  if (replayError) throw replayError;
}

export function resetOfflineReplayCoordinatorForTests(): void {
  status = "idle";
  deferredInvalidations.clear();
}
